import {
  ImportOptions,
  ProgressCb,
} from "../";
import type { DSVRowString } from "d3";
import { dsvFormat } from "d3";
import {
  castRowLonLat,
  castRowWKT,
  castRowGeoJSON,
  castRowPolyline,
  EnforcedLonLatOptions,
  EnforcedWKTOptions,
} from "./shared";
import { JsonObject, JsonValue } from "type-fest";

interface Scores {
  latitudeScore: number;
  longitudeScore: number;
}

type ColumnWithScore = {
  column: string;
} & Scores;

// https://github.com/d3/d3-dsv#autoType
export function autoType(
  input: DSVRowString,
  options: ImportOptions["csvOptions"]
): JsonObject {
  const object = input as JsonObject;
  for (const key in object) {
    let value: JsonValue = (object[key] as string).trim();
    let number;
    if (!value) value = null;
    else if (value === "true") value = true;
    else if (value === "false") value = false;
    else if (!isNaN((number = +value))) value = number;
    else continue;
    object[key] = value;
  }
  return object;
}

function sortByScore(
  columnsWithScores: ColumnWithScore[],
  scoreKey: keyof Scores
) {
  const [winner] = columnsWithScores
    .filter((column) => column[scoreKey] > 0)
    .sort((b, a) => a[scoreKey] - b[scoreKey]);
  return winner;
}

export function detectColumns(columns: string[]): ImportOptions["csvOptions"] {
  const columnsWithScores: ColumnWithScore[] = columns.map((column) => {
    return {
      column,
      latitudeScore: scoreColumn(column, /(Lat)(itude)?/gi),
      longitudeScore: scoreColumn(column, /(L)(on|ng)(gitude)?/gi),
    };
  });

  // Case-insensitive check for WKT and polyline columns
  const polylineColumn = columns.find(col => col.toLowerCase() === "polyline")
  const hasPolylineColumn = polylineColumn !== undefined;
  const wktColumn = columns.find(col => col.toLowerCase() === "wkt")
  const hasWktColumn = wktColumn !== undefined;

  const latitudeHeader = sortByScore(columnsWithScores, "latitudeScore");
  const longitudeHeader = sortByScore(columnsWithScores, "longitudeScore");

  const singleColumn = (columns.length === 1 && columns[0]) || "";

  return {
    kind: hasPolylineColumn ? "polyline" : hasWktColumn ? "wkt" : "lonlat",
    delimiter: ",",
    latitudeHeader: latitudeHeader?.column || singleColumn,
    longitudeHeader: longitudeHeader?.column || singleColumn,
    geometryHeader: hasPolylineColumn ? "polyline" : wktColumn || singleColumn,
    sheet: "",
    autoType: true,
  };
}

function scoreColumn(column: string, regex: RegExp) {
  const match = regex.exec(column.toLowerCase());
  return match ? match[0].length : 0;
}

export async function csvToGeoJSON(
  csv: string,
  options: ImportOptions["csvOptions"],
  progress: ProgressCb
): Promise<GeoJSON.FeatureCollection> {
  if (!options) throw new Error("Options should not be undefined");
  const { kind, delimiter } = options;

  let i = 0;
  const rows = dsvFormat(delimiter).parse(csv);
  const features: GeoJSON.Feature[] = [];

  // Preflight checks
  switch (kind) {
    case "geojson":
    case "wkt":
    case "polyline":
    case "lonlat": {
      break;
    }
  }

  progress({ total: rows.length, done: 0 });

  for (const row of rows) {
    const castRow: JsonObject = options.autoType
      ? autoType(row, options)
      : (row as JsonObject);
    switch (kind) {
      case "lonlat": {
        const feature = castRowLonLat(
          castRow,
          EnforcedLonLatOptions.parse(options)
        );
        if (feature) {
          features.push(feature);
        }
        break;
      }
      case "wkt": {
        const feature = castRowWKT(castRow, EnforcedWKTOptions.parse(options));
        if (feature) {
          features.push(feature);
        }
        break;
      }
      case "geojson": {
        const feature = castRowGeoJSON(
          castRow,
          EnforcedWKTOptions.parse(options)
        );
        if (feature) {
          features.push(feature);
        }
        break;
      }
      case "polyline": {
        const feature = castRowPolyline(
          castRow,
          EnforcedWKTOptions.parse(options)
        );
        if (feature) {
          features.push(feature);
        }
        break;
      }
    }
    progress({ total: rows.length, done: ++i });
  }

  return {
    type: "FeatureCollection",
    features: features,
  };
}
